<template>
  <div class="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50 animate-modalFade overflow-hidden">
    <div class="w-full sm:max-w-md mobile-modal-panel bg-gray-800 text-gray-300 rounded-t-xl sm:rounded-lg p-4 sm:p-5 flex flex-col gap-4 relative shadow-lg border border-gray-700 animate-scaleIn overflow-hidden">
      <!-- Header -->
      <div class="flex items-center justify-between flex-shrink-0 relative z-30">
        <div class="flex items-center gap-2">
          <img :src="villager.img" class="w-8 rounded">
          <h2 class="text-base sm:text-lg font-semibold text-gray-100">
            อัปเกรดชาวบ้าน: <span class="text-indigo-200">{{ villager?.name }}</span>
          </h2>
        </div>
        <button type="button"
          @click.stop="cancelModal('header-close')"
          class="p-2 rounded-md text-gray-400 hover:text-indigo-200 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-opacity-50"
          :disabled="isProcessing"
          aria-label="ปิด"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
      <!-- Loading Overlay -->
      <div v-if="isLoadingAuth || isLoading" class="absolute inset-0 z-20 rounded-t-xl sm:rounded-lg flex items-center justify-center bg-gray-900 bg-opacity-70">
        <div class="animate-spin h-10 w-10 rounded-full border-4 border-indigo-500 border-t-transparent"></div>
      </div>
      <!-- Tab Navigation -->
      <div class="flex border-b border-gray-700 flex-shrink-0">
        <button type="button"
          v-for="tab in tabs"
          :key="tab.id"
          @click="switchTab(tab.id)"
          class="px-3 sm:px-4 py-2 text-sm font-medium transition-colors duration-200 flex items-center gap-2"
          :class="activeTab === tab.id
            ? 'text-indigo-200 border-b-2 border-indigo-700'
            : 'text-gray-400 hover:text-indigo-300'"
        >
          <i :class="tab.icon" class="text-lg"></i>
          <span class="hidden md:inline">{{ tab.name }}</span>
        </button>
      </div>
      <!-- Error -->
      <div v-if="error" class="text-center text-red-300 text-xs sm:text-sm">
        {{ error }}
      </div>
      <!-- Content -->
      <div class="flex-1 min-h-0 overflow-y-auto overscroll-contain space-y-4 pr-1">
        <!-- LEVEL TAB -->
        <div v-if="activeTab === 'level'" class="space-y-3">
          <!-- Compact Level Info -->
          <div class="bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl p-3 border border-gray-700 flex items-center justify-between text-center shadow-md">
            <div class="flex-1">
              <div class="text-xs text-gray-400">ปัจจุบัน</div>
              <div class="text-lg font-bold text-white">{{ level }}</div>
            </div>
            <i class="fas fa-arrow-right text-indigo-400 mx-2"></i>
            <div class="flex-1" v-if="level < 11">
              <div class="text-xs text-gray-400">ถัดไป</div>
              <div class="text-lg font-bold text-indigo-200">{{ level + 1 }}</div>
            </div>
            <div class="flex-1" v-if="level < 11">
              <div class="text-xs text-gray-400">ได้รับ</div>
              <div class="text-lg font-bold text-green-300">+20 แต้ม</div>
            </div>
          </div>
          <div v-if="level >= 11" class="text-center text-xs text-red-300 p-3 bg-gray-900 rounded-xl border border-gray-700 shadow-md">
            <i class="fas fa-exclamation-triangle mr-1"></i>เลเวลสูงสุดแล้ว
          </div>
          <div v-else class="space-y-3">
            <div v-if="level >= 10" class="text-center text-xs text-yellow-300 bg-gray-900 p-2 rounded-md border border-yellow-600">
              <i class="fas fa-coins mr-1"></i>ค่าอัปเกรด: {{ cashCost }} cash
            </div>
            <div v-if="level < 10" class="flex items-center gap-2 bg-gray-900 p-2 rounded-md border border-gray-700">
              <i class="fas fa-toggle-on text-indigo-400"></i>
              <select v-model="paymentType" class="flex-1 px-2 py-1 bg-gray-900 text-gray-300 text-xs border-none focus:outline-none">
                <option value="payment">ใช้ทรัพยากร</option>
                <option value="item">ใช้ไอเท็ม</option>
              </select>
            </div>
            <div v-if="(level < 10 && paymentType === 'payment') || level >= 10" class="grid grid-cols-1 gap-2 text-xs sm:text-sm">
              <button type="button" v-if="level < 10 && paymentType === 'payment'" @click="paymentMethodLevel = 'diamond'" class="w-full flex items-center justify-center gap-2 p-2 bg-gray-900 rounded-md border" :class="paymentMethodLevel === 'diamond' ? 'border-indigo-500 bg-indigo-900' : 'border-gray-700 hover:border-indigo-500'" transition-all text-xs>
                <i class="fas fa-gem text-blue-400"></i> ใช้ {{ diamondCost }} เพชร (เหลือ {{ diamond }})
              </button>
              <button type="button" v-if="level < 10 && paymentType === 'payment'" @click="paymentMethodLevel = 'gold'" class="w-full flex items-center justify-center gap-2 p-2 bg-gray-900 rounded-md border" :class="paymentMethodLevel === 'gold' ? 'border-indigo-500 bg-indigo-900' : 'border-gray-700 hover:border-indigo-500'" transition-all text-xs>
                <i class="fas fa-g text-yellow-400"></i> ใช้ {{ goldCost }} ทอง (เหลือ {{ gold }})
              </button>
              <button type="button" v-if="level >= 10" @click="paymentMethodLevel = 'cash'" class="w-full flex items-center justify-center gap-2 p-2 bg-gray-900 rounded-md border" :class="paymentMethodLevel === 'cash' ? 'border-indigo-500 bg-indigo-900' : 'border-gray-700 hover:border-indigo-500'" transition-all text-xs>
                <i class="fas fa-money-bills text-green-400"></i> ใช้ {{ cashCost }} cash (เหลือ {{ cash }})
              </button>
            </div>
            <div v-if="level < 10 && paymentType === 'item'">
              <div v-if="requirements.length === 0" class="text-center text-xs text-yellow-300 p-2 bg-gray-900 rounded-md border border-yellow-600">
                <i class="fas fa-info-circle mr-1"></i>ไม่มีไอเท็มสำหรับอัปเกรด
              </div>
              <div v-else class="space-y-2">
                <p class="text-gray-300 text-xs text-center mb-2">เลือกไอเท็ม {{ quantity }} ชิ้นเพื่ออัปเกรด (ชื่อต้องไม่ซ้ำ)</p>
                <div v-if="availableItems.length === 0" class="text-center text-xxs text-yellow-300 " >
                  <i class="fas fa-info-circle mr-1"></i>ไม่มีไอเท็มสำหรับอัปเกรด (คุณไม่มีไอเท็มใดๆ จากรายการที่ต้องการ)
                </div>
                <div v-else class="space-y-2 text-xxs h-40 overflow-auto" id="style-1">
                  <div
                    v-for="invItem in availableItems"
                    :key="invItem.id"
                    @click="toggleSelection(invItem.id)"
                    class="flex items-center gap-2 bg-gray-700 p-1 rounded cursor-pointer hover:bg-gray-600 transition-colors"
                    :class="{
                      'border-indigo-500 bg-indigo-900': selectedItemIds.includes(invItem.id),
                      'bg-red-700 cursor-not-allowed pointer-events-none': !selectedItemIds.includes(invItem.id) && selectedNames.includes(invItem.name)
                    }"
                  >
                    <span class="flex-1 truncate">
                      {{ invItem.name }} <span v-if="invItem.level">+{{ invItem.level }}</span> <i v-if="invItem.transfer" class="fa-solid fa-right-left text-green-500 ml-1"></i>
                    </span>
                    <input
                      type="checkbox"
                      :checked="selectedItemIds.includes(invItem.id)"
                      class="h-3 w-3 text-indigo-600 focus:ring-indigo-500 border-gray-600 rounded pointer-events-none"
                    />
                  </div>
                </div>
                <p class="text-gray-300 text-xs text-center mt-2">เลือกแล้ว: {{ selectedItemIds.length }} / {{ quantity }}</p>
              </div>
            </div>
          </div>
        </div>
        <!-- STATS TAB -->
        <div v-if="activeTab === 'stats'" class="space-y-4">
          <!-- Summary -->
          <div class="bg-gray-900 rounded-xl p-4 border border-gray-700">
            <div class="flex items-center justify-between text-sm mb-2">
              <div class="text-indigo-200">แต้มที่มี: <span class="font-semibold text-white">{{ remainingPoints }}</span></div>
              <div class="text-indigo-200">ใช้แล้ว: <span class="font-semibold text-indigo-300">{{ usedPoints }}</span> / {{ statPoints }}</div>
            </div>
            <div class="h-2 bg-gray-800 rounded overflow-hidden border border-gray-700">
              <div class="h-full bg-indigo-600 transition-all duration-300" :style="{ width: progressWidth }"></div>
            </div>
          </div>
          <!-- Stat List -->
          <div class="grid grid-cols-1 gap-3">
            <div
              v-for="stat in statOrder"
              :key="stat"
              class="flex items-center justify-between rounded-md p-3 text-sm border border-gray-700 bg-gray-900 hover:bg-gray-800 hover:border-indigo-500 transition-all duration-200"
            >
              <div class="flex items-center gap-3">
                <i :class="statMeta[stat]?.icon || 'fas fa-plus-circle'" class="text-indigo-400 text-lg"></i>
                <span class="font-medium text-gray-100">{{ statMeta[stat]?.label || labelMap[stat] }}</span>
              </div>
              <div class="flex items-center gap-2">
                <button type="button"
                  @click="decreaseStat(stat)"
                  :disabled="(tempStats[stat] || 0) <= (stats[stat] || 0) || isProcessing"
                  class="w-8 h-8 flex items-center justify-center bg-red-900 hover:bg-red-800 text-white rounded-md text-base border border-red-700 disabled:opacity-50 transition-all"
                  aria-label="ลดค่า"
                >-</button>
                <input
                  type="number"
                  v-model.number="tempStats[stat]"
                  class="w-16 text-center bg-gray-800 text-white rounded text-base border border-gray-600 focus:border-indigo-500 focus:outline-none transition-all"
                  :min="stats[stat] || 0"
                  step="1"
                  @input="clampStat(stat)"
                  @keydown.enter.prevent.stop
                  aria-label="ค่าค่าสถานะ"
                />
                <button type="button"
                  @click="increaseStat(stat)"
                  :disabled="remainingPoints <= 0 || (tempStats[stat] || 0) >= 99 || isProcessing"
                  class="w-8 h-8 flex items-center justify-center bg-indigo-900 hover:bg-indigo-800 text-white rounded-md text-base border border-indigo-700 disabled:opacity-50 transition-all"
                  aria-label="เพิ่มค่า"
                >+</button>
              </div>
            </div>
          </div>
        </div>
        <!-- RESET TAB -->
        <div v-if="activeTab === 'reset'" class="space-y-4">
          <div class="bg-gray-900 rounded-xl p-4 border border-gray-700 text-center">
            <div class="text-sm text-gray-300 mb-1">เลเวล</div>
            <div class="text-xl font-bold text-white">{{ level }}</div>
            <div class="text-sm text-gray-300 mt-2">แต้มที่จะได้รับคืน: <span class="font-semibold text-white">{{ currentUsedPoints }}</span></div>
          </div>
          <div class="text-center text-sm text-yellow-300 p-4 bg-gray-900 rounded-xl border border-gray-700">
            การรีสเตตัสจะคืนแต้มทั้งหมดและรีเซ็ตสเตตัสเป็น 0 โดยใช้ 50 เพชร หรือ 5 cash
          </div>
          <div v-if="!showConfirmReset" class="space-y-2">
            <div class="flex justify-center gap-4">
              <label class="flex items-center gap-2 px-4 py-2 bg-gray-900 rounded-md border border-gray-700 cursor-pointer hover:border-indigo-500 transition-all">
                <input type="radio" v-model="paymentMethodReset" value="diamond" class="form-radio text-indigo-600">
                <span class="text-sm">เพชร ({{ diamond }})</span>
              </label>
              <label class="flex items-center gap-2 px-4 py-2 bg-gray-900 rounded-md border border-gray-700 cursor-pointer hover:border-indigo-500 transition-all">
                <input type="radio" v-model="paymentMethodReset" value="cash" class="form-radio text-indigo-600">
                <span class="text-sm">Cash ({{ cash }})</span>
              </label>
            </div>
          </div>
          <div v-else class="text-center text-sm text-red-300 p-4 bg-gray-900 rounded-xl border border-gray-700">
            คุณแน่ใจหรือไม่? การรีสเตตัสจะคืนแต้ม {{ currentUsedPoints }} และรีเซ็ตสเตตัสเป็น 0
          </div>
        </div>
      </div>
      <!-- Actions -->
      <div class="flex-shrink-0 space-y-2 pt-2 border-t border-gray-700 safe-area-bottom">
        <div v-if="activeTab === 'level' && level < 11">
          <button type="button"
            @click.stop="confirmLevelUpgrade"
            :disabled="isProcessing || !canAffordLevel"
            class="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-md text-sm font-medium border border-blue-700 transition-all duration-200 disabled:opacity-60 flex items-center justify-center"
          >
            <span v-if="!isProcessing">ยืนยันอัปเกรดเลเวล</span>
            <div v-else class="animate-spin h-5 w-5 rounded-full border-2 border-t-transparent"></div>
          </button>
        </div>
        <div v-if="activeTab === 'stats'">
          <button type="button"
            @click.stop="confirmStatsUpgrade"
            :disabled="remainingPoints === statPoints || isProcessing || usedPoints === 0"
            class="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-md text-sm font-medium border border-blue-700 transition-all duration-200 flex items-center justify-center"
          >
            <span v-if="!isProcessing">ยืนยันการอัพสเตตัส</span>
            <div v-else class="animate-spin h-5 w-5 rounded-full border-2 border-t-transparent"></div>
          </button>
        </div>
        <div v-if="activeTab === 'reset'">
          <button type="button"
            v-if="!showConfirmReset"
            @click="showConfirmReset = true"
            :disabled="isProcessing || !canAffordReset || currentUsedPoints === 0"
            class="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-md text-sm font-medium border border-blue-700 transition-all duration-200 disabled:opacity-60"
          >รีสเตตัส</button>
          <button type="button"
            v-else
            @click.stop="confirmReset"
            :disabled="isProcessing || !canAffordReset"
            class="w-full bg-red-600 hover:bg-red-700 text-white py-3 rounded-md text-sm font-medium border border-red-700 transition-all duration-200 disabled:opacity-60 flex items-center justify-center"
          >
            <span v-if="!isProcessing">ยืนยันการรีสเตตัส</span>
            <div v-else class="animate-spin h-5 w-5 rounded-full border-2 border-t-transparent"></div>
          </button>
        </div>
        <button type="button"
          @click.stop="cancelModal('footer-cancel')"
          :disabled="isProcessing"
          class="w-full bg-gray-700 hover:bg-gray-600 text-white py-3 rounded-md text-sm font-medium border border-gray-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >ยกเลิก</button>
      </div>
      <!-- Messages -->
      <div v-if="successMessage" class="mt-2 text-center text-indigo-300 text-sm">
        {{ successMessage }}
      </div>
      <div v-if="errorMessage" class="mt-2 text-center text-red-300 text-sm">
        {{ errorMessage }}
      </div>
    </div>
  </div>
</template>
<script>
import getUser from '@/composables/getUser';
import { usePlayerStore } from '@/composables/usePlayerStore';
import { projectFirestore, projectFunctions } from '@/firebase/config';
import { useInventoryStore } from '@/composables/useInventoryStore';
import gameItemsData from '@/data/gameItems.json';
import gameVillagersData from '@/data/gameVillagers.json';
import { ref, watch, computed, onUnmounted } from 'vue';
export default {
  name: 'UpgradeVillagerModal',
  props: { villager: { type: Object, required: true } },
  emits: ['close', 'updated'],
  setup(props, { emit }) {
    const { user, isLoadingAuth } = getUser();
    const playerStore = usePlayerStore();
    const stats = ref({});
    const tempStats = ref({});
    const statPoints = ref(0);
    const cash = computed(() => playerStore.userData?.cash || 0);
    const diamond = computed(() => playerStore.characterData?.diamond || 0);
    const gold = computed(() => playerStore.characterData?.gold || 0);
    const level = ref(1);
    const isLoading = ref(true);
    const error = ref(null);
    const isProcessing = ref(false);
    const successMessage = ref('');
    const errorMessage = ref('');
    const gameActions = projectFunctions.httpsCallable('gameActions');
    const activeTab = ref('level');
    const tabs = ref([
      { id: 'level', name: 'อัพเกรดเลเวล', icon: 'fas fa-star' },
      { id: 'stats', name: 'อัพเกรดสเตตัส', icon: 'fas fa-chart-bar' },
      { id: 'reset', name: 'รีสเตตัส', icon: 'fas fa-undo' },
    ]);
    const statOrder = ['stamina', 'str', 'agi', 'int', 'luck', 'defense'];
    const labelMap = { stamina: 'Stamina (sta)', str: 'STR (str)', agi: 'AGI (agi)', int: 'INT (int)', luck: 'Luck (luck)', defense: 'Defense (def)' };
    const statMeta = {
      stamina: { label: 'Stamina', icon: 'fas fa-heart' },
      str: { label: 'STR', icon: 'fas fa-dumbbell' },
      int: { label: 'INT', icon: 'fas fa-brain' },
      agi: { label: 'AGI', icon: 'fas fa-running' },
      luck: { label: 'Luck', icon: 'fas fa-clover' },
      defense: { label: 'Defense', icon: 'fas fa-shield-alt' },
    };
    const showConfirmReset = ref(false);
    const paymentMethodLevel = ref('diamond');
    const paymentMethodReset = ref('diamond');
    const paymentType = ref('item');
    const requirements = ref([]); // upgradeItemNames from template
    const quantity = ref(1); // upgradeQuantity from template
    const selectedItemIds = ref([]);
    const localInventory = ref([]);
    let unsubscribeVillager = null;
    const usedPoints = computed(() =>
      statOrder.reduce((sum, k) => sum + Math.max(0, (tempStats.value[k] || 0) - (stats.value[k] || 0)), 0)
    );
    const remainingPoints = computed(() => Math.max(0, statPoints.value - usedPoints.value));
    const currentUsedPoints = computed(() => statOrder.reduce((sum, k) => sum + (stats.value[k] || 0), 0));
    const progressWidth = computed(() => !statPoints.value ? '0%' : Math.min(100, Math.floor((usedPoints.value / statPoints.value) * 100)) + '%');
    const diamondCost = computed(() => template?.value?.diamondCosts?.[level.value - 1] || 0);
    const goldCost = computed(() => template?.value?.goldCosts?.[level.value - 1] || 0);
    const cashCost = computed(() => {
      if (level.value === 10) return 30;
      return 0; // No cash for <10
    });
    const canAffordLevel = computed(() => {
      if (level.value >= 10) {
        return cash.value >= cashCost.value;
      } else {
        if (paymentType.value === 'payment') {
          if (paymentMethodLevel.value === 'diamond') return diamond.value >= diamondCost.value;
          if (paymentMethodLevel.value === 'gold') return gold.value >= goldCost.value;
        } else {
          return hasEnoughMaterials.value;
        }
      }
      return false;
    });
    const canAffordReset = computed(() => paymentMethodReset.value === 'diamond' ? diamond.value >= 50 : cash.value >= 5);
    const availableItems = computed(() => {
      return localInventory.value
        .filter(invItem => requirements.value.includes(invItem.name))
        .sort((a, b) => (a.level || 0) - (b.level || 0));
    });
    const selectedNames = computed(() => {
      return selectedItemIds.value.map(sid => {
        const sItem = localInventory.value.find(i => i.id === sid);
        return sItem ? sItem.name : null;
      }).filter(name => name !== null);
    });
    const hasEnoughMaterials = computed(() => {
      return selectedItemIds.value.length === quantity.value && selectedNames.value.length === quantity.value;
    });
    const template = ref(null); // To store gameVillagers template
    const switchTab = async (tab) => { 
      activeTab.value = tab; 
      showConfirmReset.value = false; 
      if (tab === 'level' && level.value < 10 && paymentType.value === 'item') {
        await fetchUpgradeData();
      }
    };
    watch(level, (newLevel) => {
      if (newLevel >= 10) {
        paymentType.value = 'payment';
        paymentMethodLevel.value = 'cash';
      } else if (paymentType.value === 'payment' && !['diamond', 'gold'].includes(paymentMethodLevel.value)) {
        paymentMethodLevel.value = 'diamond';
      }
    });
    watch(paymentType, (newType) => {
      // เลเวล 10 ขึ้นไปต้องคงวิธีจ่ายเป็น Cash ห้าม watcher เปลี่ยนกลับเป็นเพชร
      if (newType === 'payment' && level.value < 10) {
        paymentMethodLevel.value = 'diamond';
      }
    });
    watch(
      user,
      async (newUser) => {
        if (newUser) {
          try {
            unsubscribeVillager = projectFirestore.collection('camps').doc(newUser.uid).onSnapshot(
              async (doc) => {
                if (doc.exists) {
                  const data = doc.data();
                  const villagerData = data.villagers?.[props.villager.id];
                  if (villagerData) {
                    const states = villagerData.states || {};
                    statPoints.value = states.statPoints || 0;
                    stats.value = states.upgradeStats || Object.fromEntries(statOrder.map(k => [k, 0]));
                    tempStats.value = { ...stats.value };
                    level.value = states.level || props.villager.template?.level || 1;
                    if (level.value < 10 && paymentType.value === 'item') {
                      await fetchUpgradeData();
                    }
                  } else {
                    error.value = 'ไม่พบข้อมูลชาวบ้าน';
                  }
                } else {
                  error.value = 'ไม่พบข้อมูลค่าย';
                }
                isLoading.value = false;
              },
              (err) => { error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูลชาวบ้าน: ' + err.message; isLoading.value = false; }
            );
          } catch (err) {
            error.value = 'ไม่สามารถเชื่อมต่อฐานข้อมูลได้: ' + err.message;
            isLoading.value = false;
          }
        } else {
          isLoading.value = false;
          error.value = 'กรุณาล็อกอินก่อน';
        }
      },
      { immediate: true }
    );
    const inventoryStore = useInventoryStore();

    const updateLocalInventory = () => {
      const bagData = inventoryStore.bagData;
      const itemsObj = bagData?.items || {};
      localInventory.value = Object.entries(itemsObj).map(([instanceId, itemData]) => {
        const tpl = gameItemsData[itemData.templateId] || {};
        return {
          id: instanceId,
          instanceId,
          name: tpl.name || itemData.templateId,
          templateId: itemData.templateId,
          ...tpl,
          ...itemData.states,
        };
      });
    };

    watch(() => inventoryStore.bagData, updateLocalInventory, { immediate: true });

    const fetchUpgradeData = async () => {
      try {
        template.value = gameVillagersData[props.villager.templateId] || {};
        requirements.value = template.value.upgradeItemNames || [];
        quantity.value = template.value.upgradeQuantity || 1;
        // localInventory is now updated via watch on inventoryStore.bagData
      } catch (err) {
        errorMessage.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูลไอเท็ม: ' + err.message;
      }
    };
    const toggleSelection = (id) => {
      const index = selectedItemIds.value.indexOf(id);
      if (index !== -1) {
        selectedItemIds.value.splice(index, 1);
      } else if (selectedItemIds.value.length < quantity.value) {
        const item = localInventory.value.find(i => i.id === id);
        if (item && !selectedNames.value.includes(item.name)) {
          selectedItemIds.value.push(id);
        }
      }
    };
    const autoSelect = () => {
      // Simple auto-select: pick first quantity unique names
      const selected = [];
      const usedNames = new Set();
      for (const item of availableItems.value) {
        if (!usedNames.has(item.name) && selected.length < quantity.value) {
          selected.push(item.id);
          usedNames.add(item.name);
        }
      }
      selectedItemIds.value = selected;
    };
    const increaseStat = (stat) => {
      if (remainingPoints.value > 0 && (tempStats.value[stat] || 0) < 99) {
        tempStats.value = { ...tempStats.value, [stat]: (tempStats.value[stat] || 0) + 1 };
      }
    };
    const decreaseStat = (stat) => {
      const original = stats.value[stat] || 0;
      if ((tempStats.value[stat] || 0) > original) {
        tempStats.value = { ...tempStats.value, [stat]: tempStats.value[stat] - 1 };
      }
    };
    const clampStat = (stat) => {
      const original = stats.value[stat] || 0;
      let val = Number(tempStats.value[stat]);
      if (!Number.isFinite(val)) val = original;
      if (val < original) val = original;
      if (!Number.isInteger(val)) val = Math.floor(val);
      let usedWithout = 0;
      statOrder.forEach((s) => {
        if (s !== stat) usedWithout += Math.max(0, (tempStats.value[s] || 0) - (stats.value[s] || 0));
      });
      const maxForThis = original + (statPoints.value - usedWithout);
      val = Math.min(val, Math.min(99, maxForThis));
      tempStats.value[stat] = val;
    };
    const confirmLevelUpgrade = async () => {
      if (isProcessing.value || !user.value || level.value >= 11) return;
      if (level.value >= 10 && paymentMethodLevel.value !== 'cash') {
        errorMessage.value = 'การอัปเกรดเลเวลนี้ต้องชำระด้วย Cash';
        return;
      }
      isProcessing.value = true; successMessage.value = ''; errorMessage.value = '';
      try {
        let materialItemIds = [];
        if (level.value < 10 && paymentType.value === 'item') {
          if (selectedItemIds.value.length === quantity.value) {
            materialItemIds = selectedItemIds.value;
          } else {
            throw new Error('ไม่มีไอเท็มที่เลือกครบตามจำนวน');
          }
        }
        const result = await gameActions({
          action: 'villagerActions',
          subAction: 'upgradeLevel',
          villagerId: props.villager.id,
          paymentMethod: (level.value >= 10 || paymentType.value === 'payment') ? paymentMethodLevel.value : null,
          materialItemIds: materialItemIds
        });
        if (result.data.success) { 
          successMessage.value = result.data.message || 'อัปเกรดเลเวลสำเร็จ'; 
          // Remove used items from local inventory
          localInventory.value = localInventory.value.filter(item => !materialItemIds.includes(item.id));
          // Reset selected
          selectedItemIds.value = [];
          // Data will update via onSnapshot
        }
        else { throw new Error(result.data.message || 'การอัปเกรดเลเวลล้มเหลว'); }
      } catch (err) {
        errorMessage.value = err.message || 'เกิดข้อผิดพลาดในการอัปเกรดเลเวล';
      } finally {
        isProcessing.value = false;
      }
    };
    const confirmStatsUpgrade = async () => {
      if (isProcessing.value || !user.value || usedPoints.value === 0) return;
      isProcessing.value = true; successMessage.value = ''; errorMessage.value = '';
      try {
        const updatedStats = {};
        statOrder.forEach((s) => {
          const base = stats.value[s] || 0;
          const cur = tempStats.value[s] || 0;
          updatedStats[s] = cur > base ? (cur > 99 ? 99 : cur) : base;
        });
        const result = await gameActions({
          action: 'villagerActions',
          subAction: 'upgradeStats',
          villagerId: props.villager.id,
          stats: updatedStats
        });
        if (result.data.success) {
          // Data will update via onSnapshot
          successMessage.value = result.data.message || 'อัพเกรดสเตตัสสำเร็จ';
        } else { throw new Error(result.data.message || 'การอัพเกรดสเตตัสล้มเหลว'); }
      } catch (err) {
        errorMessage.value = err.message || 'เกิดข้อผิดพลาดในการอัพเกรดสเตตัส';
      } finally {
        isProcessing.value = false;
      }
    };
    const confirmReset = async () => {
      if (isProcessing.value || !user.value) return;
      isProcessing.value = true; successMessage.value = ''; errorMessage.value = '';
      try {
        const result = await gameActions({
          action: 'villagerActions',
          subAction: 'resetStats',
          villagerId: props.villager.id,
          paymentMethod: paymentMethodReset.value
        });
        if (result.data.success) {
          successMessage.value = result.data.message || 'รีสเตตัสสำเร็จ';
          showConfirmReset.value = false;
          // Data will update via onSnapshot
        } else { throw new Error(result.data.message || 'การรีสเตตัสล้มเหลว'); }
      } catch (err) {
        errorMessage.value = err.message || 'เกิดข้อผิดพลาดในการรีสเตตัส';
      } finally {
        isProcessing.value = false;
      }
    };
    const resetEdits = () => { tempStats.value = { ...stats.value }; };
    const cancelModal = (source = 'unknown') => {
      // ป้องกัน touch/click หลุดไปโดนปุ่มยกเลิกระหว่างส่งคำสั่งอัปเกรด
      if (isProcessing.value) return;

      // ถ้ามี Stat ที่ยังไม่ยืนยัน ต้องถามก่อนปิดเพื่อไม่ให้ค่าที่กรอกหาย
      if (activeTab.value === 'stats' && usedPoints.value > 0) {
        const shouldClose = window.confirm('คุณยังไม่ได้ยืนยันการอัปเกรดสเตตัส ต้องการปิดและทิ้งค่าที่กรอกหรือไม่?');
        if (!shouldClose) return;
      }

      console.info('[UpgradeVillagerModal] close', {
        source,
        villagerId: props.villager?.id,
        activeTab: activeTab.value,
        level: level.value,
      });

      resetEdits();
      showConfirmReset.value = false;
      emit('close');
      emit('updated'); // Emit updated only on close
    };
    onUnmounted(() => {
      if (unsubscribeVillager) unsubscribeVillager();
    });
    return {
      // data
      stats, tempStats, statPoints, cash, diamond, gold, level, isLoading, isLoadingAuth, error,
      isProcessing, successMessage, errorMessage, villager: props.villager,
      // ui
      activeTab, tabs, statOrder, labelMap, statMeta, showConfirmReset, paymentMethodLevel, paymentMethodReset,
      remainingPoints, usedPoints, currentUsedPoints, progressWidth, canAffordLevel, canAffordReset, diamondCost, goldCost, cashCost,
      paymentType, requirements, quantity, selectedItemIds, localInventory, availableItems, hasEnoughMaterials, selectedNames,
      // methods
      increaseStat, decreaseStat, clampStat, confirmLevelUpgrade, confirmStatsUpgrade, confirmReset, cancelModal, switchTab,
      toggleSelection, autoSelect
    };
  },
};
</script>
<style scoped>
/* Mobile modal viewport */
.mobile-modal-panel {
  max-height: 100vh;
  max-height: 100dvh;
}
.safe-area-bottom { padding-bottom: max(0.5rem, env(safe-area-inset-bottom)); }
.overscroll-contain { overscroll-behavior: contain; }
button { touch-action: manipulation; -webkit-tap-highlight-color: transparent; }

@media (min-width: 640px) {
  .mobile-modal-panel {
    max-height: calc(100vh - 2rem);
    max-height: calc(100dvh - 2rem);
  }
}
.max-h-96 { max-height: 24rem; }
.overflow-y-auto { overflow-y: auto; }
/* Animations */
.animate-spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes modalFade { from { opacity: 0; } to { opacity: 1; } }
.animate-modalFade { animation: modalFade 0.3s ease-out; }
@keyframes scaleIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
.animate-scaleIn { animation: scaleIn 0.3s ease-out; }
/* Scrollbar */
.overflow-y-auto::-webkit-scrollbar { width: 5px; }
.overflow-y-auto::-webkit-scrollbar-track { background: #374151; border-radius: 10px; }
.overflow-y-auto::-webkit-scrollbar-thumb { background: #4b5563; border-radius: 10px; }
.overflow-y-auto::-webkit-scrollbar-thumb:hover { background: #6b7280; }
/* Buttons */
button:disabled { opacity: 0.6; cursor: not-allowed; }
button:not(:disabled):active { transform: scale(0.98); }
/* text-2xs for Tailwind 2.x */
.text-2xs { font-size: 0.7rem; line-height: 0.95rem; }
/* Form Radio */
.form-radio { accent-color: #6366f1; } /* indigo-500 */
/* Custom Scrollbar for materials */
.scrollbar-thin::-webkit-scrollbar { width: 5px; }
.scrollbar-thin::-webkit-scrollbar-track { background: #2d3748; border-radius: 10px; }
.scrollbar-thin::-webkit-scrollbar-thumb { background: #4a5568; border-radius: 10px; }
.scrollbar-thin::-webkit-scrollbar-thumb:hover { background: #6b7280; }
</style>